// src/hooks/useLogin.js
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import * as Yup from "yup";
import { login as loginAction } from "../store/authSlice";
import authService from "../services/authService";

const phoneRegExp =
  /^((\+[1-9]{1,4}[ \-]*)|(\([0-9]{2,3}\)[ \-]*)|([0-9]{2,4})[ \-]*)*?[0-9]{3,4}?[ \-]*[0-9]{3,4}?$/;

const useLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isOtpVisible, setIsOtpVisible] = useState(false);
  const [isOtpLoading, setIsOtpLoading] = useState(false);

  const formatPhoneNumber = (mobile_number) =>
    mobile_number.startsWith("+") ? mobile_number : `+91${mobile_number}`;

  // Step 1: Request OTP
  const requestOtp = async (mobile_number) => {
    setIsOtpLoading(true);
    const formattedNumber = formatPhoneNumber(mobile_number);

    try {
      await toast.promise(
        authService.requestOtp({ phoneNumber: formattedNumber }),
        {
          loading: "Sending OTP...",
          success: (data) => {
            setIsOtpVisible(true);
            return "OTP sent successfully";
          },
          error: (error) => {
            setIsOtpVisible(false);
            const message = error?.response?.data?.message || "";
            
            if (
              message.toLowerCase().includes("does not have the right interface") ||
              message.toLowerCase().includes("access denied") ||
              message.toLowerCase().includes("not found")
            ) {
              return "You do not have access to this system";
            }
            
            return message || "Failed to send OTP";
          },
        },
        {
          id: "otp-send",
          position: "top-center",
          duration: 3000,
        }
      );
    } catch (err) {
      setIsOtpVisible(false);
    } finally {
      setIsOtpLoading(false);
    }
  };

  // Step 2: Verify OTP & login
  const verifyOtp = async (mobile_number, otp) => {
    const formattedNumber = formatPhoneNumber(mobile_number);

    try {
      await toast.promise(
        authService.verifyOtp({ phoneNumber: formattedNumber, otp }),
        {
          loading: "Verifying OTP...",
          success: (data) => {
            dispatch(
              loginAction({
                token: data.accessToken,
                refreshToken: data.refreshToken,
                user: data.user,
              })
            );
            navigate("/");
            return "Login Successful";
          },
          error: (err) => {
            const message = err?.response?.data?.message || "";
            
            if (message.toLowerCase().includes("invalid otp")) {
              return "Invalid OTP. Please try again";
            }
            
            if (
              message.toLowerCase().includes("does not have the right interface") ||
              message.toLowerCase().includes("access denied")
            ) {
              return "You do not have access to this system";
            }
            
            return message || "Invalid OTP";
          },
        },
        {
          position: "top-center",
          duration: 3000,
        }
      );
    } catch (err) {
      console.error("OTP verification failed:", err);
    }
  };

  const initialValues = { mobile_number: "", otp: "" };

  const validationSchema = Yup.object({
    mobile_number: Yup.string()
      .required("Mobile number is required")
      .matches(phoneRegExp, "Phone number is not valid")
      .min(10, "Too short")
      .max(10, "Too long"),
    otp: Yup.string().when([], {
      is: () => isOtpVisible,
      then: (schema) =>
        schema
          .required("OTP is required")
          .min(6, "OTP must be 6 digits")
          .max(6, "OTP must be 6 digits"),
    }),
  });

  const onSubmit = async (values, { setSubmitting }) => {
    await verifyOtp(values.mobile_number, values.otp);
    setSubmitting(false);
  };

  return {
    requestOtp,
    verifyOtp,
    isOtpVisible,
    isOtpLoading,
    initialValues,
    validationSchema,
    onSubmit,
  };
};

export default useLogin;
